import { supabase } from '../lib/supabase';
import { smsService } from './smsService';

export const registrationService = {
  // ============================================================================
  // REGISTRATION OPERATIONS
  // ============================================================================

  async createMinimalDraft(mobileNumber, pocName) {
    const { data, error } = await supabase.rpc('create_minimal_draft_with_session', {
      mobile_num: mobileNumber,
      poc_name: pocName || 'Draft'
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async createDraft(mobileNumber, pocName, pocEmail, ageCategory, schoolId, schoolEmail, city) {
    const { data, error } = await supabase.rpc('create_draft_with_session', {
      mobile_num: mobileNumber,
      poc_name: pocName,
      poc_email: pocEmail,
      age_category_val: ageCategory,
      school_id_val: schoolId,
      school_email_val: schoolEmail,
      city_val: city
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async getDraftByMobile(mobileNumber) {
    const { data, error } = await supabase.rpc('get_draft_by_mobile_with_session', {
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    return data ? (typeof data === 'string' ? JSON.parse(data) : data) : null;
  },

  async updateRegistration(registrationId, updates) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    // Use RPC function to set session and update in the same transaction
    // This ensures the session variable is available for RLS policies
    const { data, error } = await supabase?.rpc('update_registration_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber,
      reg_updates: updates
    });

    if (error) {
      console.error('Update registration error:', error);
      // Provide better error message for RLS/permission issues
      if (error.code === 'PGRST116' || error.message?.includes('Cannot coerce') || error.message?.includes('not found')) {
        throw new Error('Permission denied. The registration may belong to a different mobile number. Please refresh the page and try again.');
      }
      throw error;
    }
    
    if (!data) {
      throw new Error('Registration update failed. Please refresh the page and try again.');
    }
    
    // RPC returns JSONB, convert to object
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async submitRegistration(registrationId) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('submit_registration_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    
    const result = typeof data === 'string' ? JSON.parse(data) : data;
    
    // Send SMS notification after successful submission
    if (result?.mobile_number && result?.registration_number) {
      try {
        await smsService.sendRegistrationStatusSMS(
          result.mobile_number,
          result.registration_number,
          'submitted'
        );
      } catch (smsError) {
        // Don't fail the submission if SMS fails - just log it
        console.error('Failed to send submission SMS:', smsError);
      }
    }
    
    return result;
  },

  async getRegistrationsByMobile(mobileNumber) {
    // Use RPC function that sets session and queries in the same connection
    // This ensures RLS policies work correctly even with connection pooling
    const { data, error } = await supabase?.rpc('get_registrations_by_mobile', {
      mobile_num: mobileNumber
    });

    if (error) throw error;
    
    // Return data as-is - the function already returns all columns plus schools
    return data || [];
  },

  async getRegistrationDetails(registrationId) {
    // Check if user is authenticated admin (has auth session)
    // Admins use email/password auth, not mobile numbers
    const { data: { session } } = await supabase?.auth?.getSession();
    const isAdmin = !!session?.user;
    
    // For mobile-based users, get mobile number from localStorage
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    
    // For admins, pass NULL as mobile_num - RLS will use auth.uid() check
    // For mobile users, pass mobile number - RLS will use session variable check
    const { data, error } = await supabase?.rpc('get_registration_details', {
      reg_id: registrationId,
      mobile_num: isAdmin ? null : mobileNumber
    });

    if (error) {
      console.error('Error fetching registration details:', error);
      throw error;
    }
    
    // RPC returns JSONB, Supabase client may convert it to object
    const registration = typeof data === 'string' ? JSON.parse(data) : data;
    
    // Log for debugging - remove in production
    console.log('Fetched registration from Supabase:', {
      id: registration?.id,
      status: registration?.status,
      rejection_reason: registration?.rejection_reason,
      registration_number: registration?.registration_number,
      school_name: registration?.schools?.name,
      player_count: registration?.players?.length
    });
    
    return registration;
  },

  // ============================================================================
  // COACH OPERATIONS
  // ============================================================================

  async addCoach(registrationId, coachData) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('insert_coach_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber,
      coach_data: {
        name: coachData?.name,
        tshirtSize: coachData?.tshirtSize,
        isPrimary: coachData?.isPrimary || false,
        email: coachData?.email || null,
        phone: coachData?.phone || null
      }
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async updateCoach(coachId, updates) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('update_coach_with_session', {
      coach_id: coachId,
      mobile_num: mobileNumber,
      coach_updates: {
        name: updates?.name,
        tshirtSize: updates?.tshirtSize,
        isPrimary: updates?.isPrimary,
        email: updates?.email,
        phone: updates?.phone
      }
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async deleteCoach(coachId) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { error } = await supabase.rpc('delete_coach_with_session', {
      coach_id: coachId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
  },

  async getCoachesByRegistration(registrationId) {
    // Check if user is authenticated admin
    const { data: { session } } = await supabase.auth.getSession();
    const isAdmin = !!session?.user;
    
    if (isAdmin) {
      // Admin users - use direct query (RLS uses auth.uid())
      const { data, error } = await supabase
        .from('coaches')
        .select('*')
        .eq('registration_id', registrationId)
        .order('is_primary', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
    
    // Mobile users - use RPC function
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('get_coaches_by_registration_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    return data || [];
  },

  // ============================================================================
  // PLAYER OPERATIONS
  // ============================================================================

  async addPlayer(registrationId, playerData) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }

    // Use RPC function to set session and insert in the same transaction
    // This ensures the session variable is available for RLS policies
    const { data, error } = await supabase?.rpc('insert_player_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber,
      player_data: {
        firstName: playerData?.firstName,
        middleName: playerData?.middleName || null,
        lastName: playerData?.lastName,
        dateOfBirth: playerData?.dateOfBirth,
        mobileNumber: playerData?.mobileNumber,
        email: playerData?.email,
        mcaId: playerData?.mcaId,
        role: playerData?.role,
        tshirtSize: playerData?.tshirtSize,
        pantSize: playerData?.pantSize,
        isCaptain: playerData?.isCaptain || false
      }
    });

    if (error) throw error;
    return data;
  },

  async updatePlayer(playerId, updates) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('update_player_with_session', {
      player_id: playerId,
      mobile_num: mobileNumber,
      player_updates: {
        firstName: updates?.firstName,
        middleName: updates?.middleName,
        lastName: updates?.lastName,
        dateOfBirth: updates?.dateOfBirth,
        mobileNumber: updates?.mobileNumber,
        email: updates?.email,
        mcaId: updates?.mcaId,
        role: updates?.role,
        tshirtSize: updates?.tshirtSize,
        pantSize: updates?.pantSize,
        isCaptain: updates?.isCaptain
      }
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  async deletePlayer(playerId) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { error } = await supabase.rpc('delete_player_with_session', {
      player_id: playerId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
  },

  async getPlayersByRegistration(registrationId) {
    // Check if user is authenticated admin
    const { data: { session } } = await supabase.auth.getSession();
    const isAdmin = !!session?.user;
    
    if (isAdmin) {
      // Admin users - use direct query (RLS uses auth.uid())
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('registration_id', registrationId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
    
    // Mobile users - use RPC function
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('get_players_by_registration_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    return data || [];
  },

  async setCaptain(registrationId, playerId) {
    if (!registrationId || !playerId) {
      throw new Error('Registration ID and Player ID are required');
    }
    
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('set_captain_with_session', {
      reg_id: registrationId,
      player_id: playerId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  // ============================================================================
  // PLAYER COUNT CALCULATION
  // ============================================================================

  async getRegistrationWithPlayerCount(registrationId) {
    try {
      const registration = await this.getRegistrationDetails(registrationId);
      const players = await this.getPlayersByRegistration(registrationId);
      
      return {
        ...registration,
        playerCount: players?.length || 0
      };
    } catch (error) {
      console.error('Error getting registration with player count:', error);
      throw error;
    }
  },

  // ============================================================================
  // COMPLETION PERCENTAGE
  // ============================================================================

  calculateCompletionPercentage(registration) {
    let completed = 0;
    const total = 6; // Matches declaration page checklist (6 items)

    const playerCount = registration?.players?.length || 0;
    const hasPlayers = playerCount >= 11;
    
    // Match the declaration page checklist exactly:
    // On declaration page, items 1, 2, 3, 5 are ALWAYS marked as "completed"
    // So we always count those 4 as completed if basic requirements are met
    
    // 1. Team Registration - Always "completed" on declaration page
    //    Check if basic info exists (poc_name, poc_email, school_id, age_category)
    if (registration?.poc_name && registration?.poc_email && registration?.school_id && registration?.age_category) {
      completed++;
    }
    
    // 2. Player Registration - Always "completed" on declaration page
    //    Check if 11+ players exist
    if (hasPlayers) {
      completed++;
    }
    
    // 3. Document Upload - Always "completed" on declaration page
    //    On declaration page, this is always marked as completed
    //    For dashboard: if user has 11+ players and basic info, assume they've reached declaration page
    //    and documents are uploaded (matching declaration page behavior)
    if (hasPlayers && registration?.poc_name && registration?.school_id) {
      completed++;
    }
    
    // 4. Captain Selection - "completed" if captain selected, else "pending"
    const hasCaptain = registration?.captain_player_id || 
                       (registration?.players && registration?.players?.some(p => p.is_captain));
    if (hasCaptain) {
      completed++;
    }
    
    // 5. Declaration Review - Always "completed" on declaration page
    //    This is just viewing the declaration, so if we have players, this is done
    if (hasPlayers) {
      completed++;
    }
    
    // 6. Signed Declaration Upload - "completed" if uploaded, else "pending"
    if (registration?.declaration_uploaded) {
      completed++;
    }

    return Math.round((completed / total) * 100);
  },

  // ============================================================================
  // ADMIN OPERATIONS
  // ============================================================================

  async getAllRegistrations(filters = {}) {
    let query = supabase?.from('registrations')?.select(`
        *,
        schools (
          id,
          name,
          city
        )
      `)?.order('created_at', { ascending: false });

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.city) {
      query = query?.eq('city', filters?.city);
    }
    if (filters?.ageCategory) {
      query = query?.eq('age_category', filters?.ageCategory);
    }
    if (filters?.schoolId) {
      query = query?.eq('school_id', filters?.schoolId);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  },

  async approveRegistration(registrationId, reviewerId, sendSMS = true) {
    const { data, error } = await supabase?.from('registrations')?.update({
        status: 'approved',
        reviewed_by: reviewerId,
        reviewed_at: new Date()?.toISOString()
      })?.eq('id', registrationId)?.select()?.single();

    if (error) throw error;

    // Send SMS notification if requested
    if (sendSMS && data?.mobile_number && data?.registration_number) {
      try {
        await smsService.sendRegistrationStatusSMS(
          data.mobile_number,
          data.registration_number,
          'approved'
        );
      } catch (smsError) {
        // Don't fail the approval if SMS fails - just log it
        console.error('Failed to send approval SMS:', smsError);
      }
    }

    return data;
  },

  async rejectRegistration(registrationId, reviewerId, reason, sendSMS = true) {
    const { data, error } = await supabase?.from('registrations')?.update({
        status: 'rejected',
        reviewed_by: reviewerId,
        reviewed_at: new Date()?.toISOString(),
        rejection_reason: reason
      })?.eq('id', registrationId)?.select()?.single();

    if (error) throw error;

    // Send SMS notification if requested
    if (sendSMS && data?.mobile_number && data?.registration_number) {
      try {
        await smsService.sendRegistrationStatusSMS(
          data.mobile_number,
          data.registration_number,
          'rejected'
        );
      } catch (smsError) {
        // Don't fail the rejection if SMS fails - just log it
        console.error('Failed to send rejection SMS:', smsError);
      }
    }

    return data;
  },

  async resetRegistrationForResubmission(registrationId) {
    // First clear the signed declaration since data has changed
    try {
      await this.removeSignedDeclaration(registrationId);
    } catch (error) {
      console.error('Error clearing declaration during resubmission:', error);
      // Continue even if clearing declaration fails
    }
    
    // Then reset status and clear rejection fields
    const { data, error } = await supabase
      .from('registrations')
      .update({
        status: 'draft',
        rejection_reason: null,
        reviewed_by: null,
        reviewed_at: null,
        updated_at: new Date().toISOString()
      })
      .eq('id', registrationId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // ============================================================================
  // PLAYER DOCUMENT OPERATIONS
  // ============================================================================

  // Map UI document type to database enum
  mapDocumentType(uiType) {
    const mapping = {
      'aadhar': 'aadhar_card',
      'birthCertificate': 'birth_certificate',
      'passportPhoto': 'passport_photo',
      'schoolId': 'school_id_card',
      'bonafide': 'bonafide_certificate'
    };
    return mapping[uiType] || uiType;
  },

  // Upload file to Supabase storage
  async uploadPlayerDocument(playerId, file, documentType) {
    // Note: Storage operations don't need RPC, session not needed for storage
    const fileExt = file?.name?.split('.').pop();
    const fileName = `${playerId}/${documentType}_${Date.now()}.${fileExt}`;
    const filePath = `player-documents/${fileName}`;

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase?.storage
      ?.from('player-documents')
      ?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      if (uploadError.message?.includes('row-level security') || uploadError.message?.includes('RLS')) {
        throw new Error(
          'Storage RLS Policy Error: The storage bucket policy is blocking file uploads.\n\n' +
          'Please ensure the "player-documents" bucket exists and has proper RLS policies set up.'
        );
      }
      throw uploadError;
    }

    // Get signed URL
    const { data: urlData, error: urlError } = await supabase?.storage
      ?.from('player-documents')
      ?.createSignedUrl(fileName, 3600);

    if (urlError) {
      console.error('Error creating signed URL:', urlError);
    }

    return {
      filePath: filePath,
      fileUrl: urlData?.signedUrl || null,
      fileName: fileName
    };
  },

  // Get signed URL for an existing player document
  async getPlayerDocumentSignedUrl(filePath) {
    // Extract file name from path
    const fileName = filePath.replace('player-documents/', '');
    
    // Generate signed URL (1 hour expiration)
    const { data: urlData, error } = await supabase?.storage
      ?.from('player-documents')
      ?.createSignedUrl(fileName, 3600);
    
    if (error) {
      console.error('Error creating signed URL:', error);
      return null;
    }
    
    return urlData?.signedUrl || null;
  },

  // Save document record to database
  async savePlayerDocument(playerId, documentType, filePath, fileSize) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const dbDocumentType = this.mapDocumentType(documentType);
    
    const { data, error } = await supabase.rpc('save_player_document_with_session', {
      player_id_val: playerId,
      mobile_num: mobileNumber,
      document_type_val: dbDocumentType,
      file_path_val: filePath,
      file_size_val: fileSize
    });
    
    if (error) throw error;
    return typeof data === 'string' ? JSON.parse(data) : data;
  },

  // Get all documents for a player
  async getPlayerDocuments(playerId) {
    // Check if user is authenticated admin
    const { data: { session } } = await supabase.auth.getSession();
    const isAdmin = !!session?.user;
    
    if (isAdmin) {
      // Admin users - use direct query (RLS uses auth.uid())
      const { data, error } = await supabase
        .from('player_documents')
        .select('*')
        .eq('player_id', playerId)
        .order('uploaded_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
    
    // Mobile users - use RPC function
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('get_player_documents_with_session', {
      player_id_val: playerId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    return data || [];
  },

  // Delete player document
  async deletePlayerDocument(playerId, documentType) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const dbDocumentType = this.mapDocumentType(documentType);
    
    // First get the file path for storage deletion
    const { data: documents, error: fetchError } = await supabase.rpc('get_player_documents_with_session', {
      player_id_val: playerId,
      mobile_num: mobileNumber
    });
    
    if (fetchError) throw fetchError;
    
    const document = documents?.find(doc => doc.document_type === dbDocumentType);
    const filePath = document?.file_path;
    
    // Delete from database using RPC
    const { data: deletedDoc, error } = await supabase.rpc('delete_player_document_with_session', {
      player_id_val: playerId,
      mobile_num: mobileNumber,
      document_type_val: dbDocumentType
    });
    
    if (error) throw error;
    
    // Delete file from storage if it exists
    if (filePath) {
      const fileName = filePath.replace('player-documents/', '');
      const { error: storageError } = await supabase?.storage
        ?.from('player-documents')
        ?.remove([fileName]);
      
      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Continue even if storage delete fails
      }
    }
    
    return deletedDoc;
  },

  // ============================================================================
  // DECLARATION UPLOAD OPERATIONS
  // ============================================================================

  // Upload signed declaration to Supabase storage
  async uploadSignedDeclaration(registrationId, file) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found in session. Please refresh the page and try again.');
    }
    
    const fileExt = file?.name?.split('.').pop();
    const fileName = `${registrationId}/signed_declaration_${Date.now()}.${fileExt}`;
    const filePath = `signed-declarations/${fileName}`;

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase?.storage
      ?.from('signed-declarations')
      ?.upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      if (uploadError.message?.includes('row-level security') || uploadError.message?.includes('RLS')) {
        throw new Error(
          'Storage RLS Policy Error: The storage bucket policy is blocking file uploads.\n\n' +
          'Please ensure the "signed-declarations" bucket exists and has proper RLS policies set up.'
        );
      }
      throw uploadError;
    }

    // Get signed URL
    const { data: urlData, error: urlError } = await supabase?.storage
      ?.from('signed-declarations')
      ?.createSignedUrl(fileName, 3600);

    if (urlError) {
      console.error('Error creating signed URL:', urlError);
    }

    // Update registration record using RPC
    const { data, error } = await supabase.rpc('update_declaration_file_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber,
      file_path_val: filePath
    });

    if (error) {
      console.error('Error updating registration:', error);
      // If file was uploaded but DB update failed, clean up the file
      if (uploadData) {
        try {
          await supabase?.storage?.from('signed-declarations')?.remove([fileName]);
          console.log('Cleaned up uploaded file after DB update failure');
        } catch (cleanupError) {
          console.error('Failed to cleanup uploaded file:', cleanupError);
        }
      }
      throw new Error(`Failed to save declaration record: ${error.message}`);
    }

    const result = typeof data === 'string' ? JSON.parse(data) : data;

    return {
      filePath: filePath,
      fileUrl: urlData?.signedUrl || null,
      fileName: fileName,
      registration: result
    };
  },

  // Get signed declaration URL for a registration
  async getSignedDeclarationUrl(registrationId) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    const { data, error } = await supabase.rpc('get_declaration_file_path_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    
    const result = typeof data === 'string' ? JSON.parse(data) : data;
    
    if (!result?.declaration_file_path || !result?.declaration_uploaded) return null;

    // Extract file name from path
    const fileName = result.declaration_file_path.replace('signed-declarations/', '');
    
    // Get signed URL (works for private buckets)
    const { data: urlData, error: urlError } = await supabase?.storage
      ?.from('signed-declarations')
      ?.createSignedUrl(fileName, 3600);

    if (urlError) {
      console.error('Error creating signed URL for declaration:', urlError);
      return null;
    }

    return urlData?.signedUrl || null;
  },

  // Remove signed declaration from storage and database
  async removeSignedDeclaration(registrationId) {
    const mobileNumber = typeof window !== 'undefined' ? localStorage.getItem('userMobile') : null;
    if (!mobileNumber) {
      throw new Error('Mobile number not found. Please log in again.');
    }
    
    // Get current declaration file path
    const { data: fileData, error: fetchError } = await supabase.rpc('get_declaration_file_path_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (fetchError) throw fetchError;
    
    const filePath = typeof fileData === 'string' ? JSON.parse(fileData)?.declaration_file_path : fileData?.declaration_file_path;
    
    // Remove file path from database using RPC
    const { data, error } = await supabase.rpc('remove_declaration_file_with_session', {
      reg_id: registrationId,
      mobile_num: mobileNumber
    });
    
    if (error) throw error;
    
    // Delete file from storage if it exists
    if (filePath) {
      const fileName = filePath.replace('signed-declarations/', '');
      const { error: storageError } = await supabase?.storage
        ?.from('signed-declarations')
        ?.remove([fileName]);
      
      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Continue even if storage delete fails
      }
    }
    
    return typeof data === 'string' ? JSON.parse(data) : data;
  },
};